import { Box, Home, Layers3, Menu, Moon, Plus, Settings, Sun, Tag, X } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Brand } from './brand';
import { Button } from './ui/button';
import { useTheme } from '@/hooks/use-theme';
import { cn } from '@/lib/utils';
import { api, isPrivateMode } from '@/lib/api';

const navItems = [
  { to: '/', label: 'Pulpit', icon: Home, end: true },
  { to: '/products', label: 'Obserwowane', icon: Tag, end: false },
  { to: '/collections', label: 'Kolekcje', icon: Layers3, end: false },
  { to: '/add', label: 'Dodaj wpis', icon: Plus, end: false },
  { to: '/settings', label: 'Ustawienia', icon: Settings, end: false },
];

function Sidebar() {
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const account=useQuery({queryKey:['account'],queryFn:api.account,enabled:isPrivateMode});
  return <aside className="sidebar flex flex-col p-5 pl-6">
    <Brand />
    <nav aria-label="Główna nawigacja" className="mt-10 flex flex-col gap-1">
      {navItems.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} className={({ isActive }) => cn('flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground', (isActive || to==='/products' && location.pathname.startsWith('/queries/')) && 'bg-muted text-foreground shadow-[inset_3px_0_0_var(--primary)]')}><Icon className="size-5" strokeWidth={1.75} />{label}</NavLink>)}
    </nav>
    <div className="mt-auto">
      <Button variant="ghost" className="w-full justify-start" onClick={toggle}>{theme === 'dark' ? <Sun /> : <Moon />}{theme === 'dark' ? 'Jasny motyw' : 'Ciemny motyw'}</Button>
      <div className="mt-5 flex items-center gap-3 border-t border-border pt-5"><div className="flex size-9 items-center justify-center rounded-full border border-border bg-muted text-xs font-bold">{account.data?.email[0]?.toUpperCase()??'M'}</div><div className="min-w-0"><p className="truncate text-sm font-medium">{account.data?.email??(isPrivateMode?'Moje konto':'Demo')}</p><p className="text-xs text-muted-foreground">{isPrivateMode ? 'Dane prywatne' : 'Tryb demo'}</p></div></div>
    </div>
  </aside>;
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const items = [
    { to: '/', label: 'Pulpit', icon: Home },
    { to: '/products', label: 'Obserwowane', icon: Tag },
    { to: '/add', label: 'Dodaj', icon: Plus, primary: true },
    { to: '/collections', label: 'Kolekcje', icon: Box },
  ];
  return <>
    {open ? <div className="fixed inset-0 bg-black/45 backdrop-blur-sm mobile-only" onClick={() => setOpen(false)} aria-hidden="true" /> : null}
    {open ? <section aria-label="Więcej opcji" className="surface fixed bottom-24 left-4 right-4 rounded-2xl p-4 mobile-only rise-in">
      <div className="flex items-center justify-between"><h2 className="font-semibold">Więcej</h2><Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Zamknij menu"><X /></Button></div>
      <div className="mt-2 flex flex-col gap-1"><NavLink to="/settings" onClick={() => setOpen(false)} className="flex min-h-12 items-center gap-3 rounded-xl px-3 text-sm hover:bg-muted"><Settings className="size-5" />Ustawienia</NavLink><Button variant="ghost" className="justify-start" onClick={toggle}>{theme === 'dark' ? <Sun /> : <Moon />}{theme === 'dark' ? 'Jasny motyw' : 'Ciemny motyw'}</Button></div>
    </section> : null}
    <nav aria-label="Mobilna nawigacja" className="mobile-nav">
      {items.map(({ to, label, icon: Icon, primary }) => <NavLink key={to} to={to} className={({ isActive }) => cn('flex min-h-14 flex-col items-center justify-center gap-1 text-[10px] font-medium text-muted-foreground', (isActive || to==='/products' && location.pathname.startsWith('/queries/')) && 'text-primary', primary && '-mt-8')}>
        <span className={cn('flex size-8 items-center justify-center rounded-xl', primary && 'size-14 rounded-full bg-primary text-primary-foreground shadow-xl')}><Icon className={primary ? 'size-7' : 'size-5'} strokeWidth={primary ? 2.1 : 1.8} /></span><span>{label}</span>
      </NavLink>)}
      <button className={cn('flex min-h-14 flex-col items-center justify-center gap-1 text-[10px] font-medium text-muted-foreground', (open || location.pathname === '/settings') && 'text-primary')} onClick={() => setOpen((current) => !current)}><Menu className="size-5" /><span>Więcej</span></button>
    </nav>
  </>;
}

export function AppShell() {
  return <div className="app-shell"><Sidebar /><main className="main-content"><Outlet /></main><MobileNav /></div>;
}
